(() => {
  const initializeShopByVideo = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const cards = Array.from(root.querySelectorAll('[data-video-card]'));
    const batchSize = Number(root.dataset.batchSize) || 12;
    const loadMoreButton = root.querySelector('[data-load-more]');
    const count = root.querySelector('[data-count]');
    const modal = root.querySelector('[data-modal]');
    const player = root.querySelector('[data-player]');
    const closeButton = root.querySelector('[data-modal-close]');
    const modalTitle = root.querySelector('[data-modal-title]');
    const modalPrice = root.querySelector('[data-modal-price]');
    const modalLink = root.querySelector('[data-modal-link]');
    const hostedVideos = Array.from(root.querySelectorAll('[data-hosted-video]'));
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const visibilityRatios = new Map();
    const observedHostedCards = new WeakSet();
    let visibleCount = Math.min(batchSize, cards.length);
    let returnFocus = null;
    let previousBodyOverflow = '';
    let activeVideo = null;

    const loadPoster = (card) => {
      if (card.dataset.posterRequested === 'true') return;
      card.dataset.posterRequested = 'true';
      const image = card.querySelector('[data-poster]');
      if (!image || !image.getAttribute('src')) return;

      const showImage = () => image.classList.add('is-loaded');
      const useFallback = () => {
        image.classList.remove('is-loaded');
        image.removeAttribute('src');
      };

      image.addEventListener('load', showImage, { once: true });
      image.addEventListener('error', useFallback, { once: true });
      if (image.complete) {
        if (image.naturalWidth > 0) showImage();
        else useFallback();
      }
    };

    const updateVideoControls = (video) => {
      const card = video.closest('[data-video-card]');
      const toggle = card.querySelector('[data-video-toggle]');
      const mute = card.querySelector('[data-video-mute]');
      const isPlaying = !video.paused && !video.ended;

      toggle.dataset.state = isPlaying ? 'playing' : 'paused';
      toggle.setAttribute('aria-label', `${isPlaying ? 'Pause' : 'Play'} video for ${card.dataset.title}`);
      mute.dataset.state = video.muted ? 'muted' : 'audible';
      mute.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} video for ${card.dataset.title}`);
      card.classList.toggle('is-video-active', isPlaying);
    };

    const ensureVideoSource = (video) => {
      if (video.dataset.sourceLoaded === 'true') return;
      video.dataset.sourceLoaded = 'true';
      video.preload = 'metadata';
      video.src = video.dataset.videoSrc;
      video.load();
    };

    const pauseHostedVideo = (video) => {
      if (!video) return;
      video.pause();
      if (activeVideo === video) activeVideo = null;
      updateVideoControls(video);
    };

    const pauseOtherHostedVideos = (current) => {
      hostedVideos.forEach((video) => {
        if (video !== current && !video.paused) pauseHostedVideo(video);
      });
    };

    const clearVideoError = (video) => {
      const card = video.closest('[data-video-card]');
      card.classList.remove('is-video-error');
      card.querySelector('[data-video-status]').hidden = true;
    };

    const playHostedVideo = (video, userInitiated = false) => {
      const card = video.closest('[data-video-card]');
      if (card.hidden || (!userInitiated && reducedMotionQuery.matches)) return;

      const isRetry = card.classList.contains('is-video-error');
      pauseOtherHostedVideos(video);
      if (!userInitiated) video.muted = true;
      ensureVideoSource(video);
      if (isRetry) video.load();
      clearVideoError(video);
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch((error) => {
          if (error.name !== 'AbortError') updateVideoControls(video);
        });
      }
    };

    const selectAutoplayCandidate = () => {
      if (document.hidden || reducedMotionQuery.matches || !modal.hidden) {
        pauseHostedVideo(activeVideo);
        return;
      }

      let bestVideo = null;
      let bestRatio = 0;
      hostedVideos.forEach((video) => {
        const card = video.closest('[data-video-card]');
        const ratio = visibilityRatios.get(video) || 0;
        if (!card.hidden && ratio >= 0.6 && ratio > bestRatio) {
          bestVideo = video;
          bestRatio = ratio;
        }
      });

      if (!bestVideo) {
        pauseHostedVideo(activeVideo);
        return;
      }

      if (activeVideo !== bestVideo || bestVideo.paused) playHostedVideo(bestVideo);
    };

    const autoplayObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('[data-hosted-video]');
          if (video) visibilityRatios.set(video, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        selectAutoplayCandidate();
      },
      { threshold: [0, 0.25, 0.5, 0.6, 0.75, 0.9, 1] }
    );

    const observeHostedCard = (card) => {
      const video = card.querySelector('[data-hosted-video]');
      if (!video || observedHostedCards.has(card)) return;
      observedHostedCards.add(card);
      updateVideoControls(video);
      autoplayObserver.observe(card);

      video.addEventListener('play', () => {
        pauseOtherHostedVideos(video);
        activeVideo = video;
        updateVideoControls(video);
      });
      video.addEventListener('pause', () => updateVideoControls(video));
      video.addEventListener('volumechange', () => updateVideoControls(video));
      video.addEventListener('loadeddata', () => clearVideoError(video));
      video.addEventListener('error', () => {
        const status = card.querySelector('[data-video-status]');
        if (activeVideo === video) activeVideo = null;
        card.classList.add('is-video-error');
        status.hidden = false;
        updateVideoControls(video);
      });
    };

    const refreshPagination = () => {
      const shown = Math.min(visibleCount, cards.length);
      count.textContent = `Showing ${shown} of ${cards.length} products`;
      loadMoreButton.hidden = shown >= cards.length;
    };

    const revealThrough = (nextCount) => {
      const end = Math.min(nextCount, cards.length);
      cards.slice(0, end).forEach((card) => {
        card.hidden = false;
        loadPoster(card);
        observeHostedCard(card);
      });
      visibleCount = end;
      refreshPagination();
    };

    const closeModal = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      player.replaceChildren();
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener('keydown', handleModalKeydown);
      if (returnFocus) returnFocus.focus();
      returnFocus = null;
      selectAutoplayCandidate();
    };

    const handleModalKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll('button:not([disabled]), a[href], iframe'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const openModal = (button) => {
      const card = button.closest('[data-video-card]');
      if (!card) return;

      pauseHostedVideo(activeVideo);

      const iframe = document.createElement('iframe');
      iframe.src = `https://drive.google.com/file/d/${encodeURIComponent(button.dataset.driveId)}/preview?autoplay=1`;
      iframe.title = `Video: ${card.dataset.title}`;
      iframe.allow = 'autoplay; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      player.replaceChildren(iframe);
      modalTitle.textContent = card.dataset.title;
      modalPrice.textContent = card.dataset.price;
      const isSoldOut = card.dataset.soldOut === 'true';
      modalLink.href = card.dataset.productUrl;
      modalLink.textContent = isSoldOut ? 'Sold Out' : 'Shop Now';
      modalLink.classList.toggle('sbv__cta--sold-out', isSoldOut);
      modalLink.setAttribute(
        'aria-label',
        isSoldOut ? `View sold out product ${card.dataset.title}` : `Shop ${card.dataset.title}`
      );
      returnFocus = button;
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      modal.hidden = false;
      document.addEventListener('keydown', handleModalKeydown);
      window.requestAnimationFrame(() => closeButton.focus());
    };

    root.addEventListener('click', (event) => {
      const videoToggle = event.target.closest('[data-video-toggle]');
      if (videoToggle) {
        const video = videoToggle.closest('[data-hosted-media]').querySelector('[data-hosted-video]');
        if (video.paused) playHostedVideo(video, true);
        else pauseHostedVideo(video);
        return;
      }

      const videoMute = event.target.closest('[data-video-mute]');
      if (videoMute) {
        const video = videoMute.closest('[data-hosted-media]').querySelector('[data-hosted-video]');
        video.muted = !video.muted;
        updateVideoControls(video);
        return;
      }

      const playButton = event.target.closest('[data-play]');
      if (playButton) {
        openModal(playButton);
        return;
      }

      if (event.target.closest('[data-modal-close]') || event.target === modal) {
        closeModal();
      }
    });

    loadMoreButton.addEventListener('click', () => revealThrough(visibleCount + batchSize));
    revealThrough(visibleCount);

    const handleVisibilityChange = () => {
      if (document.hidden) pauseHostedVideo(activeVideo);
      else selectAutoplayCandidate();
    };
    const handleReducedMotionChange = () => {
      pauseHostedVideo(activeVideo);
      if (!reducedMotionQuery.matches) selectAutoplayCandidate();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail.sectionId !== root.dataset.sectionId) return;
      closeModal();
      pauseHostedVideo(activeVideo);
      autoplayObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
    });
  };

  const initializeWithin = (scope) => {
    if (scope.matches && scope.matches('[data-shop-by-video]')) initializeShopByVideo(scope);
    scope.querySelectorAll('[data-shop-by-video]').forEach(initializeShopByVideo);
  };

  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
